<template>
  <div class="p-6 mx-auto my-8 max-w-3xl font-sans text-gray-800 bg-white rounded-2xl shadow-lg">
    <h1 class="mb-2 text-3xl font-extrabold text-center text-green-900 md:text-4xl">BoxBuddy Terms of Service</h1>
    <div class="flex flex-col gap-4 justify-center mb-8 text-sm text-gray-500 sm:flex-row">
      <span><span class="font-semibold">Effective Date:</span> July 15, 2025</span>
      <span><span class="font-semibold">Last Updated:</span> July 15, 2025</span>
    </div>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">1. Acceptance of Terms</h2>
      <p>
        By accessing or using the BoxBuddy mobile application ("App"), website, or services ("Services"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Services.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">2. Description of Service</h2>
      <p>
        BoxBuddy is a platform that connects users with local food vendors and independent delivery drivers. We facilitate food ordering and delivery but do not prepare food or employ delivery drivers directly. BoxBuddy acts as an intermediary platform connecting customers, vendors, and delivery partners.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">3. User Accounts</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">3.1 Account Creation</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>You must be at least 18 years old to create an account</li>
        <li>You must provide accurate, current, and complete information</li>
        <li>You are responsible for maintaining the confidentiality of your account credentials</li>
        <li>You are responsible for all activities that occur under your account</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">3.2 Account Suspension</h3>
      <p>
        We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent, abusive, or illegal activities.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">4. Ordering and Payment</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">4.1 Order Placement</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Orders are subject to vendor availability and acceptance</li>
        <li>Menu items, prices, and availability may change without notice</li>
        <li>We do not guarantee that vendors will accept your order</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">4.2 Payment Processing</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Payment is processed at the time of order placement</li>
        <li>We accept major credit cards, debit cards, and digital payment methods</li>
        <li>You authorize us to charge your selected payment method for orders, taxes, fees, and gratuities</li>
        <li>All sales are final unless otherwise specified</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">4.3 Pricing and Fees</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Menu prices are set by individual vendors</li>
        <li>Delivery fees, service fees, and applicable taxes will be displayed before order confirmation</li>
        <li>Prices may vary based on demand, location, and other factors</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">5. Delivery Services</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">5.1 Delivery Partners</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Delivery is provided by independent contractors, not BoxBuddy employees</li>
        <li>Delivery times are estimates and not guaranteed</li>
        <li>We are not responsible for delays caused by weather, traffic, or other circumstances beyond our control</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">5.2 Delivery Instructions</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>You must provide accurate delivery address and contact information</li>
        <li>Special delivery instructions should be clearly specified</li>
        <li>You are responsible for being available to receive your order</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">6. Food Safety and Quality</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">6.1 Vendor Responsibility</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Food preparation and safety are the sole responsibility of partner vendors</li>
        <li>We do not inspect, supervise, or control food preparation processes</li>
        <li>Vendors are responsible for compliance with local health regulations</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">6.2 Temperature and Handling</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>We make reasonable efforts to maintain food temperature during delivery</li>
        <li>Food quality may be affected by delivery time and environmental factors</li>
        <li>Report any food safety concerns immediately through the App</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">7. Cancellations and Refunds</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">7.1 Order Cancellation</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Orders may be cancelled before vendor acceptance without charge</li>
        <li>Cancellation after vendor acceptance may result in cancellation fees</li>
        <li>We reserve the right to cancel orders due to vendor unavailability or other issues</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">7.2 Refund Policy</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Refunds are issued at our discretion for undelivered orders or significant order errors</li>
        <li>Refunds typically take 3-5 business days to process</li>
        <li>We do not provide refunds for subjective food quality complaints</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">8. User Conduct</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">8.1 Prohibited Activities</h3>
      <p>You agree not to:</p>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Use the Services for illegal or unauthorized purposes</li>
        <li>Interfere with or disrupt the Services</li>
        <li>Attempt to gain unauthorized access to our systems</li>
        <li>Harass, abuse, or threaten vendors or delivery partners</li>
        <li>Submit false or misleading information</li>
        <li>Use automated systems to access the Services without permission</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">8.2 Content Guidelines</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Reviews and ratings must be honest and based on actual experiences</li>
        <li>Offensive, defamatory, or inappropriate content is prohibited</li>
        <li>We reserve the right to remove content that violates these guidelines</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">9. Intellectual Property</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">9.1 BoxBuddy Property</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>The BoxBuddy name, logo, and App are our intellectual property</li>
        <li>You may not use our trademarks without written permission</li>
        <li>App content, including text, graphics, and software, is protected by copyright</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">9.2 User Content</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>You retain ownership of content you submit (reviews, photos, etc.)</li>
        <li>You grant us a license to use, display, and distribute your content</li>
        <li>You represent that you have the right to share any content you submit</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">10. Privacy and Data Protection</h2>
      <p>Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference.</p>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">11. Disclaimers and Limitations</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">11.1 Service Availability</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Services are provided "as is" and "as available"</li>
        <li>We do not warrant uninterrupted or error-free service</li>
        <li>We may modify or discontinue Services at any time</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">11.2 Limitation of Liability</h3>
      <p class="font-bold uppercase">
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, BOXBUDDY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR BUSINESS INTERRUPTION.
      </p>
      <h3 class="mt-4 mb-1 text-base font-semibold">11.3 Maximum Liability</h3>
      <p>Our total liability for any claim related to the Services shall not exceed the amount you paid for the specific order giving rise to the claim.</p>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">12. Indemnification</h2>
      <p>You agree to indemnify and hold harmless BoxBuddy, its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the Services or violation of these Terms.</p>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">13. Third-Party Services</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">13.1 Vendor Relationships</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Vendors are independent businesses, not BoxBuddy partners or employees</li>
        <li>We do not control vendor operations, food quality, or business practices</li>
        <li>Disputes with vendors should be reported through the App</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">13.2 Payment Processing</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Payment processing is handled by third-party payment processors</li>
        <li>Third-party processors have their own terms and privacy policies</li>
        <li>We are not responsible for payment processor errors or failures</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">14. Governing Law and Dispute Resolution</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">14.1 Governing Law</h3>
      <p>These Terms are governed by the laws of [Your Jurisdiction] without regard to conflict of law principles.</p>
      <h3 class="mt-4 mb-1 text-base font-semibold">14.2 Dispute Resolution</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Disputes should first be addressed through our customer support</li>
        <li>Any legal disputes will be resolved through binding arbitration</li>
        <li>Class action lawsuits are prohibited</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">15. General Provisions</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">15.1 Entire Agreement</h3>
      <p>These Terms constitute the entire agreement between you and BoxBuddy regarding the Services.</p>
      <h3 class="mt-4 mb-1 text-base font-semibold">15.2 Severability</h3>
      <p>If any provision of these Terms is found unenforceable, the remaining provisions will remain in full force and effect.</p>
      <h3 class="mt-4 mb-1 text-base font-semibold">15.3 Modification</h3>
      <p>We may modify these Terms at any time by posting updated Terms in the App. Continued use constitutes acceptance of modified Terms.</p>
      <h3 class="mt-4 mb-1 text-base font-semibold">15.4 Assignment</h3>
      <p>You may not assign your rights under these Terms. We may assign our rights and obligations without restriction.</p>
      <h3 class="mt-4 mb-1 text-base font-semibold">15.5 Waiver</h3>
      <p>Our failure to enforce any provision does not constitute a waiver of that provision.</p>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">16. Contact Information</h2>
      <p>
        For questions about these Terms of Service, please contact us at:<br>
        <span class="font-semibold">BoxBuddy Support</span><br>
        Email: <a href="mailto:support@boxbuddy.me" class="text-green-700 underline">support@boxbuddy.me</a><br>
        Phone: +2347046804856<br>
        Address: Plot 108, Voera Estate, Arepo, Ogun State, Nigeria
      </p>
    </section>
  </div>
</template>

<script setup lang="ts">

</script>
